import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Phone, MessageCircle, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { Marquee } from "@/components/Marquee";
import { SearchBar } from "@/components/SearchBar";
import { PropertyCard } from "@/components/PropertyCard";
import { PropertyMap } from "@/components/PropertyMap";
import { api, AGENCY, waLink } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const HERO_IMG = "/hero-colosseo.jpg";

const HomePage = () => {
  const [allProps, setAllProps] = useState([]);
  const [sort, setSort] = useState("price_desc");
  const [showAll, setShowAll] = useState(false);
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacityFg = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/properties", { params: { sort } })
      .then((r) => setAllProps(r.data))
      .catch(() => {});
  }, [sort]);

  return (
    <div data-testid="home-page">
      <Seo
        title={t("seo.homeTitle")}
        siteName="ROMACASA"
        description={t("seo.homeDesc")}
        image="/og-image.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          name: "ROMACASA Intermediazioni Immobiliari",
          areaServed: "Roma",
          address: { "@type": "PostalAddress", streetAddress: AGENCY.address, addressLocality: "Roma", addressCountry: "IT" },
          telephone: AGENCY.phone,
          email: AGENCY.email,
        }}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden noise flex flex-col" data-testid="hero-section">
        <motion.div style={{ y: yBg }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Il Colosseo Quadrato all'EUR, Roma — sede ROMACASA" fetchpriority="high" decoding="async" className="w-full h-[120%] object-cover hero-img-zoom" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000]/45 via-[#000000]/40 to-[#000000]/85" />
        <motion.div style={{ opacity: opacityFg }} className="relative z-[2] flex-1 w-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col items-center justify-end pb-20 sm:pb-14">
          <h1 className="sr-only">{t("seo.homeH1")}</h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-5xl"
          >
            <SearchBar />
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="intro-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif-d text-3xl sm:text-4xl lg:text-5xl font-medium leading-snug max-w-2xl">
              {t("home.statement")}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 flex lg:justify-end">
            <Link
              to="/chi-siamo"
              data-testid="intro-chi-siamo-link"
              className="inline-flex items-center gap-2 bg-[#000000] hover:bg-[#2E2E2E] text-white px-7 py-4 text-sm font-medium transition-colors duration-300 group w-fit"
            >
              {t("home.scopri")}
              <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-16 sm:mt-20 h-px bg-[#E0E3E6]" data-testid="intro-divider" />
      </section>

      {/* TUTTI GLI IMMOBILI */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="all-properties-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow>{t("list.portfolio")}</Eyebrow>
            <h2 className="font-serif-d text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mt-4">{t("home.tutti")}</h2>
          </div>
          <label className="flex items-center gap-3" data-testid="sort-control">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black">{t("home.ordina")}</span>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => { setSort(e.target.value); setShowAll(false); }}
              className="border border-[#E0E3E6] bg-white px-4 py-2.5 text-sm outline-none focus:border-black transition-colors cursor-pointer"
            >
              <option value="newest">{t("home.sortNew")}</option>
              <option value="oldest">{t("home.sortOld")}</option>
              <option value="price_desc">{t("home.sortPriceDesc")}</option>
              <option value="price_asc">{t("home.sortPriceAsc")}</option>
            </select>
          </label>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(showAll ? allProps : allProps.slice(0, 6)).map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i, 5) * 0.06}>
              <PropertyCard p={p} />
            </Reveal>
          ))}
        </div>
        {!showAll && allProps.length > 6 && (
          <div className="mt-14 flex justify-center">
            <button
              data-testid="show-all-properties-button"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-3 border border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-[#FAFAF7] px-10 py-4 text-xs font-semibold uppercase tracking-[0.22em] transition-colors duration-300 group"
            >
              {t("home.vediTutto")}
              <ArrowRight size={15} className="group-hover:translate-x-1.5 transition-transform" />
            </button>
          </div>
        )}
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#2A2A2A] text-[#FAFAF7] noise" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 relative z-[2]">
          <Reveal className="max-w-2xl mb-14">
            <img src="/icon-arch-light.png" alt="Icona ROMACASA — le arcate del Colosseo Quadrato" className="w-20 h-auto mb-8" data-testid="brand-icon" />
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#E8E8E8]">{t("home.manifesto")}</p>
            <h2 className="font-serif-d text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mt-4">
              {t("home.progetto")} <span className="italic text-[#E8E8E8]">{t("home.genetico")}</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1E1E1E]">
            {["01", "02", "03", "04"].map((n, i) => (
              <Reveal key={n} delay={i * 0.08}>
                <div className="bg-[#232323] p-8 sm:p-12 h-full group hover:bg-[#2E2E2E] transition-colors duration-500">
                  <span className="font-mono-d text-sm text-[#E8E8E8]">{n}</span>
                  <h3 className="font-serif-d text-2xl sm:text-3xl font-medium mt-4 mb-4 group-hover:text-[#E8E8E8] transition-colors">{t(`ch.${i}.title`)}</h3>
                  <p className="text-[#8A94A0] font-light leading-relaxed text-sm sm:text-base">{t(`ch.${i}.text`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MAP TEASER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="map-teaser-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <Eyebrow>{t("home.territorio")}</Eyebrow>
            <h2 className="font-serif-d text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mt-4 mb-6">{t("home.mappaTitle")}</h2>
            <p className="text-[#5B6670] font-light leading-relaxed mb-8 max-w-md">{t("home.mappaText")}</p>
            <Link to="/mappa" data-testid="map-teaser-button" className="inline-flex items-center gap-2 bg-[#000000] hover:bg-[#2E2E2E] text-white px-7 py-3.5 text-sm font-medium transition-colors duration-300">
              <MapPin size={16} /> {t("home.apriMappa")}
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="aspect-[4/3]" data-testid="home-map-embed">
              <PropertyMap properties={allProps} height="100%" scrollWheel={false} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-[#F1F2F4] border-t border-[#E0E3E6] noise" data-testid="contact-cta-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 relative z-[2] text-center">
          <Reveal>
            <Eyebrow className="justify-center">{t("home.tempo")}</Eyebrow>
            <h2 className="font-serif-d text-3xl sm:text-5xl lg:text-6xl font-medium leading-tight mt-4 mb-10 max-w-3xl mx-auto">
              {t("home.salotto1")} <span className="italic text-[#000000]">{t("home.salotto2")}</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={AGENCY.phoneHref} data-testid="cta-phone" className="flex items-center gap-2.5 bg-[#000000] hover:bg-[#000000] text-white px-7 py-4 text-sm font-medium transition-colors duration-300">
                <Phone size={16} /> {AGENCY.phone}
              </a>
              <a href={waLink("Buongiorno ROMACASA, vorrei fissare un appuntamento.")} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="flex items-center gap-2.5 bg-[#25D366] hover:bg-[#1eb857] text-white px-7 py-4 text-sm font-medium transition-colors duration-300">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href={`mailto:${AGENCY.email}`} data-testid="cta-email" className="flex items-center gap-2.5 border border-[#000000] hover:bg-[#000000] hover:text-[#FAFAF7] px-7 py-4 text-sm font-medium transition-colors duration-300">
                <Mail size={16} /> {AGENCY.email}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
